import React, { useEffect, useRef, useState } from "react";
import api, { sourceLabel, formatDate } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Link2, UploadCloud, CheckCircle2, Circle, RefreshCw, Save } from "lucide-react";

export default function Crawler() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sources, setSources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  async function loadSources() {
    const r = await api.get("/sources");
    setSources(r.data);
  }
  useEffect(() => { loadSources(); }, []);

  const [progress, setProgress] = useState("");

  async function extract(e) {
    e && e.preventDefault();
    if (!url) return;
    setBusy(true);
    setResult(null);
    setProgress("");
    try {
      const r = await api.post("/crawler/extract", { url });
      // Async job path: collections return { job_id, status: 'running' }
      if (r.data.job_id && r.data.status === "running") {
        setProgress("Enumerating lots — this can take up to 3 minutes for large sales…");
        const jobId = r.data.job_id;
        const startedAt = Date.now();
        // Poll every 2.5s, cap at 5 minutes
        while (Date.now() - startedAt < 300000) {
          await new Promise((res) => setTimeout(res, 2500));
          try {
            const j = await api.get(`/crawler/extract/jobs/${jobId}`);
            if (j.data.status === "done") {
              setResult(j.data.result);
              setProgress("");
              return;
            }
            if (j.data.status === "error") {
              throw new Error(j.data.error || "Extraction failed");
            }
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            setProgress(`Extracting lots… (${elapsed}s)`);
          } catch (pollErr) {
            if (pollErr?.response?.status === 404) throw pollErr;
            // transient poll error — keep trying
          }
        }
        throw new Error("Extraction timed out after 5 minutes");
      }
      setResult(r.data);
    } catch (e) {
      toast.error("Extraction failed", { description: e.message || String(e) });
    } finally {
      setBusy(false);
      setProgress("");
    }
  }

  async function saveExtracted() {
    if (!result) return;
    setSaving(true);
    try {
      if (result.is_collection) {
        const r = await api.post("/crawler/save-collection", {
          lots: (result.lots || []).map((f) => ({ ...f, source: f.source || result.source })),
        });
        toast.success("Collection imported", {
          description: `${r.data.inserted} added · ${r.data.duplicates} already tracked · ${r.data.skipped} skipped`,
        });
      } else {
        const r = await api.post("/crawler/save", {
          source: result.source,
          fields: { ...result.fields, url: result.fields.url || url },
        });
        toast.success(r.data.duplicate ? "Already in database" : "Added to database");
      }
    } catch (e) {
      toast.error("Save failed", { description: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function triggerCrawl(key) {
    try {
      const r = await api.post(`/sources/${key}/crawl`);
      toast.success(r.data.message || "Crawl started");
      setTimeout(loadSources, 2000);
    } catch (e) {
      toast.error("Couldn't start crawl", { description: e.message });
    }
  }

  async function toggleSource(key) {
    await api.post(`/sources/${key}/toggle`);
    loadSources();
  }

  async function handleImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/lots/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Import complete", {
        description: `${r.data.inserted} added · ${r.data.updated} updated · ${r.data.skipped} skipped`,
      });
    } catch (e) {
      toast.error("Import failed", { description: e.message });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="space-y-6" data-testid="crawler-page">
      <Toaster position="top-right" richColors />
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-[color:var(--caa-charcoal)]">
          URL crawler &amp; sources
        </h1>
        <p className="text-sm text-[color:var(--caa-muted)] mt-1">
          Paste any auction URL, extract the fields, add it to the database. Or manage configured sites.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="caa-card p-6">
            <form onSubmit={extract} className="flex flex-col sm:flex-row gap-3" data-testid="crawler-form">
              <div className="flex-1 relative">
                <Link2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--caa-muted)]" />
                <input data-testid="crawler-url" value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.pickles.com.au/..."
                  className="w-full rounded-xl border caa-hairline bg-white pl-9 pr-3 py-3 text-sm outline-none focus:border-[color:var(--caa-teal-deep)]" />
              </div>
              <button data-testid="crawler-extract" type="submit" disabled={busy || !url}
                className="rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-70"
                style={{ background: "var(--caa-teal-deep)" }}>
                {busy ? "Extracting…" : "Extract"}
              </button>
            </form>
            {busy && progress && (
              <div className="mt-3 text-xs text-[color:var(--caa-muted)] flex items-center gap-2" data-testid="crawler-progress">
                <RefreshCw size={12} className="animate-spin" />
                {progress}
              </div>
            )}
          </div>

          {result && result.is_collection && (
            <div className="caa-card p-6" data-testid="crawler-collection-result">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-[color:var(--caa-charcoal)]">
                    Collection: {result.collection_title || "(untitled)"}
                  </h3>
                  <p className="text-xs mt-1 text-[color:var(--caa-muted)]">
                    Found {result.lot_count_found} lots ·{" "}
                    <span style={{ color: "var(--caa-status-live)" }}>
                      {result.lot_count_kept} classified as self-propelled cranes
                    </span>{" "}
                    · {result.lot_count_dropped} dropped
                  </p>
                </div>
                <button onClick={saveExtracted} disabled={saving || !result.lots?.length}
                  data-testid="crawler-save-collection"
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "var(--caa-teal-deep)" }}>
                  <Save size={14} /> {saving ? "Saving…" : `Add ${result.lots?.length || 0} lots`}
                </button>
              </div>
              <div className="space-y-2" data-testid="collection-kept-list">
                {(result.lots || []).map((f, i) => (
                  <div key={f.url || i} className="border caa-hairline rounded-lg p-3 flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-[color:var(--caa-charcoal)] truncate">{f.title || "(no title)"}</div>
                      <div className="text-xs text-[color:var(--caa-muted)] mt-0.5">
                        {[f.make, f.model_name, f.year].filter(Boolean).join(" · ")}
                        {f.location ? ` · ${f.location}` : ""}
                        {f.current_bid ? ` · Bid $${Number(f.current_bid).toLocaleString()}` : ""}
                      </div>
                      <a href={f.url} target="_blank" rel="noreferrer"
                        className="text-xs text-[color:var(--caa-teal-deep)] hover:underline truncate block max-w-full mt-0.5">
                        {(f.url || "").replace(/^https?:\/\//, "").slice(0, 80)}{(f.url || "").length > 80 ? "…" : ""}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              {result.dropped?.length > 0 && (
                <details className="mt-4">
                  <summary className="text-xs font-semibold text-[color:var(--caa-muted)] cursor-pointer" data-testid="collection-dropped-toggle">
                    Show {result.dropped.length} dropped (not self-propelled cranes)
                  </summary>
                  <div className="mt-2 space-y-1">
                    {result.dropped.map((d, i) => (
                      <div key={i} className="text-xs text-[color:var(--caa-muted)]">
                        · {d.title || "(no title)"} — <span className="italic">{d.reason}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {result && !result.is_collection && (
            <div className="caa-card p-6" data-testid="crawler-result">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-[color:var(--caa-charcoal)]">
                    Extracted from {sourceLabel(result.source)}
                  </h3>
                  <p className="text-xs mt-1" style={{
                    color: result.supported || result.structured
                      ? "var(--caa-status-live)"
                      : "var(--caa-status-upcoming)"
                  }}>
                    {result.supported
                      ? "Tuned parser used — high confidence"
                      : (result.note || "Generic extraction — please review")}
                  </p>
                </div>
                <button onClick={saveExtracted} disabled={saving}
                  data-testid="crawler-save"
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "var(--caa-teal-deep)" }}>
                  <Save size={14} /> {saving ? "Saving…" : "Add to database"}
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(result.fields || {}).map(([k, v]) => (
                  <FieldPreview key={k} label={k} value={v} />
                ))}
              </div>
            </div>
          )}

          <div className="caa-card p-6" data-testid="csv-import-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-bold text-[color:var(--caa-charcoal)]">Import CSV</h3>
                <p className="text-xs text-[color:var(--caa-muted)] mt-1 max-w-lg">
                  Bulk-load your existing spreadsheet. Columns: make, model, year, serial, hours,
                  km, state, location, class, capacity, sold_price, status, source, url.
                </p>
              </div>
              <label data-testid="csv-import-btn"
                className="inline-flex items-center gap-2 rounded-lg border caa-hairline bg-white px-3 py-2 text-sm font-semibold text-[color:var(--caa-charcoal)] hover:bg-[color:var(--caa-teal-soft)] cursor-pointer">
                <UploadCloud size={15} />
                {importing ? "Importing…" : "Choose CSV"}
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
              </label>
            </div>
          </div>
        </div>

        <div className="caa-card p-6" data-testid="sources-panel">
          <h3 className="font-bold text-[color:var(--caa-charcoal)] mb-3">Configured sites</h3>
          <ul className="divide-y caa-hairline">
            {sources.map((s) => (
              <li key={s.key} className="py-3" data-testid={`source-${s.key}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[color:var(--caa-charcoal)] flex items-center gap-2">
                      {s.name}
                      {s.supported ? (
                        <CheckCircle2 size={14} style={{ color: "var(--caa-status-live)" }} />
                      ) : (
                        <Circle size={14} className="text-[color:var(--caa-muted)]" />
                      )}
                    </div>
                    <div className="text-xs text-[color:var(--caa-muted)] truncate">{s.base_url}</div>
                    <div className="text-xs mt-1 text-[color:var(--caa-muted)]">
                      Status: <span className="font-semibold">{s.status}</span>
                      {s.last_crawl_at ? ` · last crawl ${formatDate(s.last_crawl_at)}` : " · never crawled"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => triggerCrawl(s.key)} disabled={!s.supported || !s.enabled}
                      data-testid={`crawl-${s.key}`}
                      className="inline-flex items-center gap-1.5 rounded-md border caa-hairline px-2 py-1 text-xs font-semibold text-[color:var(--caa-charcoal)] hover:bg-[color:var(--caa-teal-soft)] disabled:opacity-50">
                      <RefreshCw size={12} /> Crawl
                    </button>
                    <button onClick={() => toggleSource(s.key)} data-testid={`toggle-${s.key}`}
                      className="text-xs font-semibold"
                      style={{ color: s.enabled ? "var(--caa-teal-deep)" : "var(--caa-muted)" }}>
                      {s.enabled ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function _prettyLabel(k) {
  return String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function _fmtValue(k, v) {
  if (v === null || v === undefined || v === "") return "—";
  const key = String(k).toLowerCase();
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length === 1 ? "" : "s"}` : "—";
  if (typeof v === "number") {
    if (key.includes("bid") || key.includes("price")) {
      return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    if (key === "hours" || key === "km") {
      return Number(v).toLocaleString();
    }
    return String(v);
  }
  if (typeof v === "string") {
    // ISO date detection
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      try {
        return formatDate(v);
      } catch { /* fall through */ }
    }
    return v;
  }
  return JSON.stringify(v);
}

function FieldPreview({ label, value }) {
  const isImages = String(label).toLowerCase() === "images" && Array.isArray(value) && value.length;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">
        {_prettyLabel(label)}
      </div>
      {isImages ? (
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid="crawler-images">
          {value.slice(0, 6).map((src, i) => (
            <img key={i} src={src} alt="" className="w-14 h-14 object-cover rounded border caa-hairline" />
          ))}
          {value.length > 6 && (
            <span className="text-xs text-[color:var(--caa-muted)] self-center">+{value.length - 6}</span>
          )}
        </div>
      ) : (
        <div className="text-sm text-[color:var(--caa-charcoal)] break-words">
          {_fmtValue(label, value)}
        </div>
      )}
    </div>
  );
}
